<template>
  <div class="lookup-page">
    <v-card class="lookup-card">
      <div class="lookup-header">
        <div class="lookup-header__copy">
          <div class="lookup-title">Program Education</div>
          <div class="lookup-subtitle">Manage program education groupings used across preservice forms.</div>
        </div>
        <v-text-field
            v-model="search"
            class="lookup-search"
            append-icon="mdi-magnify"
            hide-details
            label="Search"
            single-line
        ></v-text-field>
        <v-btn
            v-if="canCreateEPrograms"
            class="lookup-add-button"
            depressed
            @click="addEducationalProgram()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add Program Education</span>
        </v-btn>
      </div>
      <v-data-table
          :headers="headers"
          :items="educationalPrograms.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="educationalPrograms.count"
          class="lookup-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.name`]="{ item }">
          <label for="item">{{ item.name ? item.name : "-" }}</label>
        </template>
        <template v-slot:[`item.code`]="{ item }">
          <label for="item">{{ item.code ? item.code : "-" }}</label>
        </template>
        <template v-if="canEditEPrograms" v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon class="mr-2 lookup-action-icon" @click="editEducationalProgram(item)"
                      v-on="on">mdi-circle-edit-outline
              </v-icon>
            </template>
            <span>{{ title }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.isActive`]="{ item }">
          <v-chip :color="getColor(item.isActive)" dark>
            {{ item.isActive == true ? "Active" : "Not Active" }}
          </v-chip>
        </template>

      </v-data-table>

    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="50%">
        <v-card class="lookup-dialog-card">
          <v-card-title class="lookup-dialog__title" primary-title>{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="lookup-dialog__close" color="white" text @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <AddEducationalProgram v-if="dialog" :educationalProgram="educationalProgram"
                                 @close="closeDialog()" @registerOrUpdateEProgram="registerOrUpdateEProgram">
          </AddEducationalProgram>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import {getUser} from "@/utils/helper";
import AddEducationalProgram from './add-educational-programs.vue';
import {LookupService} from "../../../common/api.service";
import {EducationalProgramService} from './educationalProgram.service';

export default {
  components: {
    AddEducationalProgram
  },
  data() {
    return {
      search: "",
      searchDebounce: null,
      loading: false,
      headers: [
        {text: "Program Education", value: "name"},
        {text: "Program Education Code", value: "code"},
        {text: "Program Education Status", value: "isActive"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      educationalPrograms: [],
      educationalProgram: null,
      dialog: false,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      title: ""
    }
  },
  computed: {
    canCreateEPrograms() {
      return getUser().permissions.some(
          (permission) =>
              ["Program Education", "Educational Programs"].includes(permission?.menuItem?.menuItemName) &&
              permission.canCreate
      );
    },
    canEditEPrograms() {
      return getUser().permissions.some(
          (permission) =>
              ["Program Education", "Educational Programs"].includes(permission?.menuItem?.menuItemName) &&
              permission.canUpdate
      );
    },
  },
  async beforeMount() {
    this.fetchEducationalPrograms();
  },
  watch: {
    options: {
      handler() {
        this.fetchEducationalPrograms();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchEducationalPrograms();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchEducationalPrograms();
      }, 400);
    },
  },
  methods: {
    async fetchEducationalPrograms() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;
      const res = await LookupService.getEducationalProgramsWP(
          page,
          itemsPerPage,
          this.search
      );
      if (res.status === 200) {
        this.loading = false;
        this.educationalPrograms = res.data;
      } else {
        this.loading = false;
        this.$toast.error(
            "There was a problem fetching program education, please try again."
        );
      }
    },
    addEducationalProgram() {
      this.educationalProgram = null;
      this.dialog = true;
      this.title = "Add Program Education";
    },
    editEducationalProgram(item) {
      this.educationalProgram = item;
      this.dialog = true;
      this.title = "Edit Program Education";
    },
    closeDialog() {
      this.dialog = false;
      this.fetchEducationalPrograms();
    },
    getColor(isActive) {
      if (isActive == false) return "red";
      else return "green";
    },
    async registerOrUpdateEProgram(educationalProgram) {
      this.loading = true;
      let res = null;

      if (educationalProgram.editMode) {
        res = await EducationalProgramService.update(
            educationalProgram.id,
            educationalProgram
        );

        if (res.status === 201) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fetchEducationalPrograms();
              this.loading = false;
              break;
            case 500:
              this.$toast.error(res.data.message);
              this.loading = false;
              break;
          }
        } else {
          this.$toast.error(
              "Unable to update program education, please try again."
          );
          this.loading = false;
        }
      } else {
        res = await EducationalProgramService.create(educationalProgram);

        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fetchEducationalPrograms();
              this.loading = false;

              break;
            case 500:
              this.$toast.error(res.data.message);
              this.loading = false;

              break;
          }
        } else {
          this.$toast.error(
              "Unable to create program education, please try again."
          );
          this.loading = false;
        }
      }
      this.loading = false;
    }
  }
}
</script>

<style scoped>
.lookup-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.lookup-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.lookup-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.lookup-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lookup-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.lookup-subtitle {
  font-size: 14px;
  color: #475569;
}

.lookup-search {
  width: 100%;
  max-width: 360px;
  justify-self: center;
  align-self: center;
}

.lookup-table {
  border-radius: 0 0 12px 12px;
}

.lookup-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.lookup-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.lookup-dialog__close {
  color: #ffffff !important;
}

.lookup-dialog__close .v-icon {
  color: #ffffff !important;
}

.lookup-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.lookup-action-icon {
  color: #1976d2 !important;
}

@media (max-width: 700px) {
  .lookup-page {
    padding: 12px;
  }

  .lookup-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .lookup-search {
    max-width: 100%;
  }

  .lookup-add-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
